import { Box, Typography } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PinDashboardButton from "@plannel/components/dashboard/PinDashboardButton";
import dateUtils from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

/*
 * DW 대시보드 공용 레이아웃 셸.
 * 페이지마다 복붙되던 매직넘버(p/gap/minHeight/탑바 mb)를 한곳에 고정한다.
 * 레퍼런스: pages/dashboard/SalesStatusDashboard.js
 */

const ROW_MIN_HEIGHT = 240;   // 차트 행 최소 높이

// 페이지 최외곽 셸 — 세로 flex, 전체 높이 채움.
export const DashboardPage = ({ children, sx }) => (
  <Box sx={{
    height: "100%", display: "flex", flexDirection: "column",
    p: 1.5, gap: 1.5, minHeight: 0, bgcolor: "background.default",
    ...sx,
  }}>
    {children}
  </Box>
);

// baseDate 배열 → 기간 { from, to }(yyyy-MM). 이미 yyyy-MM 이면 그대로, 아니면 포맷.
//   DP는 원본 날짜, IP/RP는 이미 yyyy-MM 이므로 양쪽 모두 안전하게 처리한다.
export const baseDatesToPeriod = (dates) => {
  if (!dates?.length) return null;
  const toYm = (d) => (typeof d === "string" && /^\d{4}-\d{2}$/.test(d) ? d : dateUtils.formatDate(d, "yyyy-MM"));
  return { from: toYm(dates[0]), to: toYm(dates[dates.length - 1]) };
};

// 데이터 기간 캡션(달력 아이콘 + "from ~ to") — 없으면 아무것도 렌더하지 않는다.
//   period: { from, to }. 대시보드 상단바(DashboardTopBar)와 DP/IP/RP 필터 툴바에서 공용.
export const DashboardPeriodCaption = ({ period }) => {
  if (!period) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <DateRangeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
      <Typography sx={{ fontSize: FS.SUB, color: "text.secondary" }}>{`${period.from} ~ ${period.to}`}</Typography>
    </Box>
  );
};

// 상단 바 — 우측 정렬. children(단위 토글 등) + [홈 고정 별] + 데이터 기간 캡션.
//   period: { from, to } — 없으면 캡션 생략.
//   pinViewName: 주면 홈 "고정한 대시보드" 즐겨찾기 별을 노출(= 페이지 reduxKey/viewName).
export const DashboardTopBar = ({ children, period, pinViewName }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flexShrink: 0, mb: -0.75 }}>
    {children}
    {pinViewName && <PinDashboardButton viewName={pinViewName} />}
    <DashboardPeriodCaption period={period} />
  </Box>
);

// 행 컨테이너.
//   kpi=true  → 고정 높이 KPI 행(grid, 기본 3열)
//   kpi=false → 늘어나는 차트/패널 행(flex, minHeight 하한)
export const DashboardRow = ({ children, kpi = false, columns = 3, minHeight = ROW_MIN_HEIGHT, sx }) => (
  <Box sx={kpi
    ? { display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1.5, flexShrink: 0, ...sx }
    : { display: "flex", gap: 1.5, flex: 1, minHeight, ...sx }}
  >
    {children}
  </Box>
);
